import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, CalendarDays, Video, Loader2, Clock, X, CalendarClock } from "lucide-react";

function fmt(startIso) {
  try {
    const d = new Date(startIso);
    return d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return startIso; }
}

const BLANK = { start: "", duration_min: 30, notes: "", video_call: false, client_id: "open" };

export function AdminAppointments({ clients = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [reschedule, setReschedule] = useState(null);
  const [rs, setRs] = useState({ start: "", duration_min: 30 });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/appointments"); setRows(data.appointments); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.error("Failed to load appointments:", e?.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.start) { toast.error("Please choose a date and time"); return; }
    setSaving(true);
    try {
      const payload = { start: form.start, duration_min: Number(form.duration_min), notes: form.notes, video_call: form.video_call };
      if (form.client_id && form.client_id !== "open") payload.client_id = form.client_id;
      await api.post("/admin/appointments", payload);
      toast.success(payload.client_id ? "Appointment booked for client" : "Open slot created");
      setForm(BLANK); setCreateOpen(false); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create appointment"); }
    finally { setSaving(false); }
  };

  const cancel = async (id) => {
    setBusy(id);
    try { await api.post(`/appointments/${id}/cancel`); toast.success("Appointment cancelled"); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not cancel"); }
    finally { setBusy(null); }
  };

  const doReschedule = async () => {
    if (!reschedule || !rs.start) { toast.error("Choose a new date and time"); return; }
    setBusy(reschedule.id);
    try {
      await api.patch(`/admin/appointments/${reschedule.id}`, { start: rs.start, duration_min: Number(rs.duration_min) });
      toast.success("Appointment rescheduled"); setReschedule(null); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not reschedule"); }
    finally { setBusy(null); }
  };

  const statusBadge = (s) => {
    const map = { open: "bg-brand-gold/15 text-brand-gold", booked: "bg-brand-green/10 text-brand-green", cancelled: "bg-brand-error/10 text-brand-error" };
    return <Badge className={`${map[s] || ""} border-0 capitalize`}>{s}</Badge>;
  };

  return (
    <div className="mt-6 rounded-xl border border-brand-line bg-white overflow-hidden" data-testid="admin-appointments-panel">
      <div className="px-6 py-4 border-b border-brand-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-brand-green" />
          <h2 className="font-serif text-xl text-brand-green">Appointments</h2>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid="appt-create-open">
              <CalendarPlus className="w-4 h-4 mr-1.5" /> New appointment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white" data-testid="appt-create-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-brand-green">New appointment</DialogTitle>
              <DialogDescription className="text-sm text-brand-muted">Create an open slot clients can book, or book directly with a specific client.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4 mt-2">
              <div>
                <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Date &amp; time</Label>
                <Input type="datetime-local" required value={form.start} onChange={(e) => set("start", e.target.value)}
                  className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="appt-start" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Duration (minutes)</Label>
                <Input type="number" min="15" step="15" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)}
                  className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="appt-duration" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
                  <SelectTrigger className="bg-white border-brand-line" data-testid="appt-client"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open slot (any client can book)</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  placeholder="e.g. Initial consultation" className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="appt-notes" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="vc" checked={form.video_call} onCheckedChange={(v) => set("video_call", !!v)} data-testid="appt-video" />
                <Label htmlFor="vc" className="text-sm text-brand-ink cursor-pointer flex items-center gap-1"><Video className="w-4 h-4" /> Add a video call link</Label>
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-brand-green hover:bg-brand-greenHover text-white" data-testid="appt-create-submit">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="divide-y divide-brand-line">
        {loading && <div className="px-6 py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-brand-green" /></div>}
        {!loading && rows.length === 0 && <div className="px-6 py-8 text-center text-brand-muted text-sm">No appointments yet.</div>}
        {!loading && rows.map((a) => (
          <div key={a.id} className="px-6 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-appt-${a.id}`}>
            <div className="min-w-[220px]">
              <p className="text-sm font-semibold text-brand-green flex items-center gap-2">
                {fmt(a.start_iso)} {a.video_call && <Video className="w-3.5 h-3.5 text-brand-muted" />}
              </p>
              <p className="text-xs text-brand-muted flex items-center gap-2 mt-0.5">
                <Clock className="w-3 h-3" /> {a.duration_min} min · {a.client_email ? (a.client_name || a.client_email) : "Open slot"}
              </p>
              {a.notes && <p className="text-xs text-brand-ink mt-1">{a.notes}</p>}
            </div>
            <div className="flex items-center gap-2">
              {statusBadge(a.status)}
              {a.status !== "cancelled" && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => { setReschedule(a); setRs({ start: a.start_iso, duration_min: a.duration_min }); }}
                    className="text-brand-green hover:bg-brand-admin h-8 px-2" data-testid={`admin-appt-reschedule-${a.id}`} title="Reschedule">
                    <CalendarClock className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => cancel(a.id)} disabled={busy === a.id}
                    className="text-brand-error hover:bg-red-50 h-8 px-2" data-testid={`admin-appt-cancel-${a.id}`} title="Cancel">
                    {busy === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Reschedule dialog */}
      <Dialog open={!!reschedule} onOpenChange={(o) => { if (!o) setReschedule(null); }}>
        <DialogContent className="max-w-sm bg-white" data-testid="appt-reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-green">Reschedule appointment</DialogTitle>
            <DialogDescription className="text-sm text-brand-muted">Choose a new date and time. The client will be emailed the update.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">New date &amp; time</Label>
              <Input type="datetime-local" value={rs.start} onChange={(e) => setRs((f) => ({ ...f, start: e.target.value }))}
                className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="appt-reschedule-start" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-brand-ink mb-1.5 inline-block">Duration (minutes)</Label>
              <Input type="number" min="15" step="15" value={rs.duration_min} onChange={(e) => setRs((f) => ({ ...f, duration_min: e.target.value }))}
                className="bg-white border-brand-line focus-visible:ring-brand-green" data-testid="appt-reschedule-duration" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedule(null)} className="border-brand-line text-brand-ink">Cancel</Button>
            <Button onClick={doReschedule} disabled={busy === reschedule?.id} className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid="appt-reschedule-submit">
              {busy === reschedule?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
